/**
 * The platform operator's entry (`ops.html`), and the third of this
 * repository's three.
 *
 * It is deliberately the same four things as `src/app/main.tsx` -- the theme
 * resolved once from an explicit choice or the system preference, the auth
 * provider that owns the Firebase session, the query client and `ApiClient`
 * every page reads through, and a composed router -- because the operator signs
 * in with the same Firebase session against the same API. A second auth stack
 * here would be a second thing to get wrong, and this plane's access control is
 * not in the client at all: the API answers `403` and every screen is built to
 * be correct when it does (platform design §3).
 *
 * What it does NOT share is the half that matters. It mounts `./routes`, whose
 * glob reaches only inside `src/ops/` -- never `src/app/routes/index.ts`,
 * whose glob composes the feature areas under `src/app/` into the TENANT
 * router. An ops area registered there would be statically imported by
 * `app.html`, every tenant would download the entire operator surface, and it
 * would work perfectly, which is why nobody would notice.
 * `src/ops/__tests__/boundary.test.ts` asserts that over the built chunks
 * rather than over the imports.
 *
 * The mood is `instrument`: dense, achromatic, colour reserved for data.
 * That needs no call here -- `:root` in
 * `tokens.css` IS the instrument palette and `--mood-brief-*` is the landing
 * page's separate set -- so it is stated rather than applied, and the chrome
 * here consumes the default tokens.
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from '@tanstack/react-router';

import { AuthProvider } from '../app/auth/AuthProvider';
import { AuthBridge } from '../app/session/AuthBridge';
import { createAppApiClient } from '../app/session/token';
import { setLocale, type Locale } from '../i18n';
import { applyTheme, resolveTheme, THEME_STORAGE_KEY } from '../tokens/theme';
import { opsRouter } from './routes';
import '../tokens/tokens.css';

/** The node `ops.html` offers, and the only one this entry will mount into.
 *  Exported so a test can hold the document and this module to one id rather
 *  than to two spellings that agree today. */
export const MOUNT_ID = 'ops';

/**
 * Pinned rather than inherited. `src/i18n`'s active locale is one module-level
 * value shared by every catalogue, and defaulting to it would leave this
 * entry's language a property of a file it does not own. It is also what
 * `ops.html` declares as `lang`, and the two must not be able to disagree.
 */
export const OPS_LOCALE: Locale = 'pt-BR';

/**
 * Storage throws rather than returning null in a browser configured to block
 * site data, and a theme preference is not worth failing to boot over.
 */
function storedTheme(): string | null {
  try {
    return window.localStorage.getItem(THEME_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function bootstrapOps(target: HTMLElement): void {
  applyTheme(
    document.documentElement,
    resolveTheme(storedTheme(), window.matchMedia('(prefers-color-scheme: dark)').matches),
  );
  // persist: false -- ops and the tenant app share one origin and therefore
  // one localStorage; a persisting call here would overwrite whatever a
  // future tenant-side language selector had stored, on every ops page load.
  setLocale(OPS_LOCALE, { persist: false });

  createRoot(target).render(
    <StrictMode>
      {/* Outermost: it initialises the Firebase app that `firebaseIdToken`
          reads, and the sign-in state every page below it depends on. */}
      <AuthProvider>
        <AuthBridge client={createAppApiClient()}>
          <RouterProvider router={opsRouter} />
        </AuthBridge>
      </AuthProvider>
    </StrictMode>,
  );
}

// Nothing happens on a page without the mount node. `main.tsx` is bundled into
// `ops.html` only, but a stray import must not take a page down: `createRoot`
// throws on a null container.
const container = document.getElementById(MOUNT_ID);

if (container) bootstrapOps(container);
